<template>
	<div
		:class="[/*isDisabledClassname,*/ isLabelInline, isFieldItemGrow, fieldItemClassname]"
		class="field-item"
	>
		<div
			v-if="label || labelIcon"
			class="field-label"
			:class="labelInlineClassname"
		>
			<label :class="isDisabledClassname">
				{{ label }}<i v-if="labelIcon" :class="labelIcon"></i>
				<span v-if="required">*</span>
			</label>
			<assist-tooltip
				v-if="assist"
				v-bind="assist"
			></assist-tooltip>
		</div>
		<div
			class="field"
			:class="[isUnitClassname, fieldClassname, isDisabledClassname]"
		>
			<input
				v-model="field"
				@keydown="onKeydown"
				@change="onChange"
				type="text"
				autocomplete="off"
				:disabled="disabled"
				:placeholder="placeholder"
				:aria-label="ariaLabel"
				ref="inputRef"
			>
			<div v-if="unit" class="field__unit">{{ unit }}</div>
			<div v-if="unitIcon" class="field__unit"><i :class="unitIcon"></i></div>
			<div v-if="limited !== null" class="field__limit">{{ field === null ? '0' : field.length }} / {{ limited }}</div>
			<div :class="isErrorClassname" class="field-error">{{ errorMessage }}</div>
		</div>
		<div v-if="allowSlot" class="field-slot">
			<slot></slot>
		</div>
	</div>
</template>

<script>
import {computed, inject, onMounted, onUnmounted, ref, watch} from 'vue';
import {FIELD_TYPES} from '@/models/FIELD_TYPES';
import AssistTooltip from '@/components/ui/tooltip/AssistTooltip.vue';
export default {
	components: {AssistTooltip},
	props: {
		modelValue: {
			type: [Number, String, Object],
			default: ''
		},
		type: {
			type: String,
			default: 'text'
		},
		placeholder: {
			type: String,
			default: ''
		},
		disabled: {
			type: Boolean,
			default: false
		},
		allowSlot: {
			type: Boolean,
			default: false
		},
		label: {
			type: String,
			default: ''
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		labelIcon: {
			type: [String, Object],
			default: null
		},
		required: {
			type: Boolean,
			default: false
		},
		index: {
			type: Number,
			default: null
		},
		errorMessage: {
			type: String,
			default: 'Field is required'
		},
		labelInline: {
			type: Boolean,
			default: false
		},
		unit: {
			type: [String, Object],
			default: null
		},
		unitIcon: {
			type: [String, Object],
			default: null
		},
		fieldItemClassname: {
			type: String,
			default: ''
		},
		fieldClassname: {
			type: String,
			default: ''
		},
		fieldItemGrow: {
			type: Boolean,
			default: true
		},
		returnNullIfEmpty: {
			type: Boolean,
			default: true
		},
		limited: {
			type: [Number, Object],
			default: null
		},
		numbersOnly: {
			type: Boolean,
			default: false,
		},
		maxLimit: {
      type: [Number, Object],
			default: null
		},
		assist: {
			type: Object,
			default: null
		},
		focused: {
			type: [Number, Object],
			default: null
		},
		formattingShortcuts: {
			type: Boolean,
			default: false
		}
	},
	emits: {
		'update:modelValue': () => true,
		'emit-action': () => true,
		'on-change': () => true,
		'check-field': () => true,
		'on-keydown': () => true,
		'format-shortcut': () => true,
	},
  setup(props, { emit, attrs }) {
		const emitter = inject("emitter");
		
		const isDisabledClassname = computed(() => props.disabled ? "disabled" : "");
		const isLabelInline = computed(() => props.labelInline ? "" : "field-item--column");
		const labelInlineClassname = computed(() => props.labelInline ? "" : "mb--15");
		const isUnitClassname = computed(() => props.unit || props.unitIcon ? "field--unit" : "");
		const isFieldItemGrow = computed(() => props.fieldItemGrow ? "field-item--grow" : "");
		const inputRef = ref(null);
  	const field = computed({
			get() {return props.modelValue},
			set(value) {fieldValueSet(value, settings.value)}
		});
		
		const settings = computed(() => ({
			type: props.type,
			returnNullIfEmpty: props.returnNullIfEmpty,
			limited: props.limited,
			numbers: props.numbersOnly,
			maxLimit: props.maxLimit,
			required: props.required
		}))
		function fieldValueSet(value, settings){
			const {type, numbers, returnNullIfEmpty, limited, maxLimit, required } = settings;
			if(numbers && value !== '') value = parseInt(value); // make sure value is Number
			if(type === FIELD_TYPES.FLOAT && value !== '') value = parseFloat(value);
			if(value === '' && returnNullIfEmpty) value = null; // return null if value is empty
      if(maxLimit && value > maxLimit) value = maxLimit; // make sure the max limit is not exceeded
			// prevent typing if limit has been reached
			if(limited !== null && value !== null && value.length >= limited) value = value.substring(0, limited);
			updateValue(value);
			if(required){
				clearError();
			}
		}
		function updateValue(value){
			emit("update:modelValue", value);
			emit("on-keydown", value);
		}
  	
  	const error = ref(false);
  	const isErrorClassname = computed(() => error.value ? "error" : "");
	
		// Insert a literal token (e.g. `<br>`, `&nbsp;`, `&shy;`) at the caret and
		// keep the caret after it. The DOM value is set first so Vue's v-model
		// diff sees no change and does not reset the caret to the end.
		function insertAtCaret(token){
			const el = inputRef.value;
			const current = el.value ?? '';
			const start = el.selectionStart ?? current.length;
			const end = el.selectionEnd ?? current.length;
			const next = current.slice(0, start) + token + current.slice(end);
			el.value = next;
			const caret = start + token.length;
			el.setSelectionRange(caret, caret);
			updateValue(next);
		}
		// Title formatting shortcuts (opt-in via `formattingShortcuts`). Returns
		// true when the key was handled so onKeydown stops further processing.
		function handleFormattingShortcut(event){
			const mod = event.ctrlKey || event.metaKey;
			// Shift+Enter → hard line break
			if(event.key === 'Enter' && event.shiftKey){
				event.preventDefault();
				insertAtCaret('<br>');
				return true;
			}
			// Ctrl/Cmd+Shift+Space → non-breaking space
			if(mod && event.shiftKey && (event.code === 'Space' || event.key === ' ')){
				event.preventDefault();
				insertAtCaret('&nbsp;');
				return true;
			}
			// Ctrl/Cmd+- → soft (optional) hyphen; also suppresses browser zoom-out
			if(mod && !event.shiftKey && event.key === '-'){
				event.preventDefault();
				insertAtCaret('&shy;');
				return true;
			}
			// Ctrl/Cmd+B / Ctrl/Cmd+I → let the parent toggle bold / italic
			if(mod && (event.key === 'b' || event.key === 'B')){
				event.preventDefault();
				emit('format-shortcut', 'bold');
				return true;
			}
			if(mod && (event.key === 'i' || event.key === 'I')){
				event.preventDefault();
				emit('format-shortcut', 'italic');
				return true;
			}
			// Escape → commit (already saved on input) and blur
			if(event.key === 'Escape'){
				event.preventDefault();
				inputRef.value.blur();
				return true;
			}
			return false;
		}

		function onKeydown(event){
			// fired before the setter of the field called
			let value = event.target.value;
			if(props.formattingShortcuts && handleFormattingShortcut(event)) return;
			// a modifier means this is a shortcut rather than typing, so the
			// character filters below let it through and select-all, copy, paste
			// and undo keep working in a numbers-only field
			const isShortcut = event.ctrlKey || event.metaKey || event.altKey;
			// if only numbers allowed
			// when a key that produces a character value is pressed down and its value is isNaN
			// prevent firing fieldValueSet
			if(props.numbersOnly && !isShortcut && event.key.length === 1 && isNaN(parseInt(event.key))) {
				event.preventDefault();
			}
			// if a field type is float
			// when a key that produces a character value is pressed down
			if(props.type === FIELD_TYPES.FLOAT && !isShortcut && event.key.length === 1){
				if(event.key === '.'){
					// ensure only one dot symbol is presented in value
					if(value.indexOf('.') > -1) event.preventDefault();
					if(value.indexOf('.') === -1) {
						// prevent firing fieldValueSet when the last char in value is a dot
						event.target.value = `${value}${event.key}`;
						event.preventDefault();
					}
				}
				// prevent firing fieldValueSet
				if(event.key !== '.' && isNaN(parseInt(event.key))) event.preventDefault();
			}
      if(props.maxLimit && value >= props.maxLimit) event.preventDefault();
			if(props.limited !== null && value !== null && value.length >= props.limited) event.target.value = value.substring(0, props.limited - 1);
			if(event.keyCode === 13) {
				emit("emit-action");
				inputRef.value.blur();
			}
		}
		function onChange(event){
			let value = event.target.value;
			emitOnChange(value);
		}
		function emitOnChange(value){
			emit('on-change', value);
		}
	
		function check(){
			error.value =  field.value !== null ? field.value.trim().length === 0 : true;
			emit("check-field", {field: attrs.field, error: error.value, index: props.index});
		}
		function clearError(){
			if(error.value){
				emit("check-field", {field: attrs.field, error: false, index: props.index});
			}
			error.value = false;
		}
		function clearField(){
			emit("update:modelValue", '');
		}
		// Re-focus this field on request (e.g. Enter/F2 on the selected node).
		// Only fields opting into formatting shortcuts subscribe, and at most one
		// such field (the node title) is mounted at a time. Selecting the whole
		// value mirrors the rename convention (F2 in file managers, Figma) so the
		// user can type a replacement immediately.
		function focusInput(){
			inputRef.value?.focus();
			inputRef.value?.select();
		}
		
		watch(
			() => props.focused,
			(value, oldValue) => {
				if(value && value !== oldValue){
					inputRef.value.focus();
				}
			})
	  watch(
		  () => props.required,
		  (value, oldValue) => {
				if(!value) emitter.off("checkField", check);
				if(value) emitter.on("checkField", check);
		  }
	  )
		onMounted(() => {
			if(props.disabled) return; // not subscribed to a listeners in case of disabled
			if(props.focused){
				inputRef.value.focus();
			}
			if(props.required){
				emitter.on("checkField", check);
			}
			emitter.on("clearField", clearField);
			emitter.on("clearError", clearError);
			if(props.formattingShortcuts){
				emitter.on("focusFormattingField", focusInput);
			}
		})
		onUnmounted(() => {
			if(props.disabled) return;
			if(props.required){
				emitter.off("checkField", check);
			}
			emitter.off("clearField", clearField);
			emitter.off("clearError", clearError);
			if(props.formattingShortcuts){
				emitter.off("focusFormattingField", focusInput);
			}
		})
  	
  	return{
  		field,
			inputRef,
			isErrorClassname,
			isDisabledClassname,
			isUnitClassname,
			isLabelInline,
			isFieldItemGrow,
			labelInlineClassname,
			onKeydown,
		  onChange
		}
  
  }
}
</script>

<style lang="scss">
@use "@/scss/sections/editor/elements/field/field-variables" as *;
@use "@/scss/sections/editor/elements/field/mod";
@use "@/scss/sections/editor/elements/field/field-row";
@use "@/scss/sections/editor/elements/field/field-item";
@use "@/scss/sections/editor/elements/field/field-label";
@use "@/scss/sections/editor/elements/field/field";
@use "@/scss/sections/editor/elements/field/field-slot";
@use "@/scss/base/fields/field-error";
@use "@/scss/mods/margin";
</style>
